/**
 * EvalLedger.tsx  — Slice 1e  (Drill-In Ledger)
 *
 * Assembles the five default-closed ledger drawers that appear below the
 * "above-the-fold" hero/phase-strip/video/coaching block:
 *
 *   1. Goal checks            — GoalResultsSection  (all users, all can expand)
 *   2. Phase breakdown        — CompactPhaseScores + FormScoreBreakdown  (coach gets detail)
 *   3. Movement economy       — MovementEconomySection  (coach-only)
 *   4. Visual & movement      — GeminiObservationSection + MovementAnalysis full  (all users; per G-2 decision)
 *   5. Technical diagnostics  — DebugDiagnosticsPanel contents  (all users, already default-closed)
 *
 * Role-gating contract:
 *  - Goal checks: all users, all can expand goal cards. 2026-09-21 report
 *    redesign (owner decision 2, "words-first WITH expandable details")
 *    RETIRES the prior `allowExpand={isCoach}` Q1 ruling — athletes now get
 *    the same expand interaction as coaches. The detail panel itself stays
 *    label-only/words-first (see GoalResultsSection.tsx); this is a UI
 *    interaction change only, not a new data leak (no new fields are sent
 *    to non-coach roles).
 *  - Phase breakdown drawer: all users; FormScoreBreakdown sub-section only rendered when isCoach
 *  - Movement economy drawer: only rendered when isCoach
 *  - Visual & movement: all users when data present
 *  - Technical diagnostics: all users (owner ruling 2026-07-25; default-closed is concealment)
 *
 * data-guide-section IDs:
 *  The adaptive guide's scroll-spy (useSectionSpy) reads `data-guide-section` attributes.
 *  Each drawer's outer div carries the same ID as the flat-stack wrapper it replaced.
 *
 * Nothing is deleted — every section that was in the flat stack lives in a drawer here.
 */

import {
  FlagIcon,
  ChartBarIcon,
  BoltIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import EvalLedgerDrawer from './EvalLedgerDrawer';
import GoalResultsSection from './GoalResultsSection';
import CompactPhaseScores from './CompactPhaseScores';
import FormScoreBreakdown from './FormScoreBreakdown';
import MovementEconomySection from './MovementEconomySection';
import GeminiObservationSection from './GeminiObservationSection';
import MovementAnalysis from './MovementAnalysis';
import DebugDiagnosticsPanel from './DebugDiagnosticsPanel';
import type {
  PhaseScore,
  SpatialGoalResult,
  AxisGoalResult,
  TimingGoalResult,
  RegionScore,
  EconomyPhaseData,
  EomNarrative,
  GeminiErrorObservation,
} from '@/types/api';
import type { DeterministicObservation } from './MovementAnalysis';

// ── Local helper types ────────────────────────────────────────────────────────

type GoalResults = Record<string, SpatialGoalResult | AxisGoalResult | TimingGoalResult>;

interface PricingResult {
  verdict: 'scoreable' | 'severe' | '0-class';
  dims_fired: string[];
  per_dim_scores: Record<string, {
    z: number | null;
    score: number | null;
    verdict: 'scoreable' | 'severe' | '0-class';
    Kc: number | null;
    Ks: number | null;
  }>;
  aggregate_score: number;
  is_zero_class: boolean;
  template_verification_status?: string;
}

// ── Summary-line helpers ──────────────────────────────────────────────────────

/** Count non-skipped goals in a results map. */
/**
 * Whether a goal produced no measurement at all (viewpoint observability
 * Phase 1). An abstained checkpoint carries `passed: false` because there is
 * no pass — but it is NOT a failure, and counting it as one would make the
 * summary below read "3 of 5 met" for a video where 2 checks were never
 * measured. It is excluded from both counts for the same reason `skipped`
 * already is; the coverage line and the withheld rows on the report are where
 * those checkpoints are accounted for.
 *
 * `status` is absent on every evaluation produced before the contract, which
 * reads as "computed".
 */
function isUncounted(r: GoalResults[string]): boolean {
  const skipped = 'skipped' in r ? (r as SpatialGoalResult).skipped : false;
  return !!skipped || r.status === 'abstained';
}

function countNonSkipped(results: GoalResults): number {
  return Object.values(results).filter((r) => !isUncounted(r)).length;
}

/** Count passed (and counted) goals. */
function countPassed(results: GoalResults): number {
  return Object.values(results).filter((r) => r.passed && !isUncounted(r)).length;
}

/** Build the goal-checks summary line: "5 of 6 met" or "all 3 met" */
function goalChecksSummary(results: GoalResults): string {
  const total = countNonSkipped(results);
  const passed = countPassed(results);
  if (total === 0) return 'none';
  if (passed === total) return `all ${total} met`;
  return `${passed} of ${total} met`;
}

/** Normalise a 0-1 score to integer 0-100. */
function toInt(score: number): number {
  return Math.round(score > 1 ? score : score * 100);
}

/** Build the phase-breakdown summary line from phase scores. */
function phaseBreakdownSummary(phases: PhaseScore[]): string {
  if (phases.length === 0) return '';
  const parts = phases.map((p) => {
    const label = (p.phase_label ?? p.phase_name ?? `Phase ${p.phase_index ?? ''}`).replace(/_/g, ' ');
    const score = p.form_score ?? p.spatial_score ?? p.score ?? p.overall_phase_score;
    if (score == null) return label;
    return `${label} ${toInt(score)}`;
  });
  // Keep it short: at most 3 phases inline, then "…"
  if (parts.length <= 3) return parts.join(' · ');
  return parts.slice(0, 3).join(' · ') + ' · …';
}

/** Build the economy summary line. */
function economySummary(
  eomPhases: Record<string, EconomyPhaseData> | undefined,
  eomNarrative: EomNarrative | undefined,
): string {
  // Use eom_narrative headline if present (human-readable)
  if (eomNarrative?.headline) return eomNarrative.headline;
  if (!eomPhases || Object.keys(eomPhases).length === 0) return 'no data';
  const vals = Object.values(eomPhases)
    .map((p) => p?.phase_score ?? null)
    .filter((v): v is number => v != null);
  if (vals.length === 0) return 'no data';
  const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  if (avg >= 80) return 'no wasted motion';
  if (avg >= 60) return 'some inefficiency detected';
  return 'significant wasted motion';
}

/** Build the visual & movement summary line. */
function visualMovementSummary(
  geminiObs: GeminiErrorObservation[] | null | undefined,
  detObs: DeterministicObservation[] | null | undefined,
): string {
  const parts: string[] = [];
  if (geminiObs && geminiObs.length > 0) {
    parts.push(`${geminiObs.length} visual note${geminiObs.length !== 1 ? 's' : ''}`);
  }
  if (detObs && detObs.length > 0) {
    const faults = detObs.filter((o) => !o.is_positive && !o.is_plausibility_flagged).length;
    if (faults > 0) {
      parts.push(`${faults} movement note${faults !== 1 ? 's' : ''}`);
    }
  }
  if (parts.length === 0) return 'no notes';
  return parts.join(' · ');
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface EvalLedgerProps {
  /** True when the viewing user is a coach or admin. */
  isCoach: boolean;

  // ── Drawer 1: Goal checks ────────────────────────────────────────
  spatialGoalResults?: GoalResults | null;

  // ── Drawer 2: Phase breakdown ────────────────────────────────────
  phaseScores: PhaseScore[];
  legacyRegionScores?: RegionScore[];
  /** Callback passed to CompactPhaseScores for click-to-seek. */
  onPhaseClick?: (phase: PhaseScore) => void;
  /** Whether to show BDS/JAS per-bone breakdown. Should be true only for coaches. */
  showPhaseScores?: boolean;
  showFormBreakdown?: boolean;
  formBreakdownBadge?: number;

  // ── Drawer 3: Movement economy (coach-only) ─────────────────────
  economyOfMotion?: Record<string, EconomyPhaseData>;
  eomNarrative?: EomNarrative;

  // ── Drawer 4: Visual & movement analysis ────────────────────────
  geminiObservations?: GeminiErrorObservation[] | null;
  deterministicObservations?: DeterministicObservation[] | null;
  /** True when there is no LLM coaching narrative and det. obs. are the fallback. */
  noLlmFallback?: boolean;

  // ── Drawer 5: Technical diagnostics ─────────────────────────────
  pricingResult?: PricingResult | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * EvalLedger — the Drill-In Ledger for the results-page redesign (Slice 1e).
 *
 * Five default-closed drawers that appear below the above-the-fold content
 * (hero, phase strip, video, editorial coaching + compact visual read).
 *
 * Nothing inside is deleted — every section from the flat stack lands in a drawer.
 */
function EvalLedger({
  isCoach,
  // Drawer 1
  spatialGoalResults,
  // Drawer 2
  phaseScores,
  legacyRegionScores,
  onPhaseClick,
  showPhaseScores,
  showFormBreakdown,
  formBreakdownBadge: _formBreakdownBadge,
  // Drawer 3
  economyOfMotion,
  eomNarrative,
  // Drawer 4
  geminiObservations,
  deterministicObservations,
  noLlmFallback,
  // Drawer 5
  pricingResult,
}: EvalLedgerProps) {
  // ── Drawer 1 — Goal checks ──────────────────────────────────────────────────
  const hasGoals =
    spatialGoalResults != null &&
    Object.keys(spatialGoalResults).length > 0 &&
    countNonSkipped(spatialGoalResults) > 0;

  // ── Drawer 2 — Phase breakdown ──────────────────────────────────────────────
  const hasPhases = phaseScores.length > 0;

  // ── Drawer 3 — Movement economy (coach-only) ────────────────────────────────
  const hasEconomy =
    isCoach &&
    ((economyOfMotion != null && Object.keys(economyOfMotion).length > 0) ||
      eomNarrative != null);

  // ── Drawer 4 — Visual & movement analysis ──────────────────────────────────
  const hasGemini = geminiObservations != null && geminiObservations.length > 0;
  const hasDetObs = deterministicObservations != null && deterministicObservations.length > 0;
  const hasVisualMovement = hasGemini || hasDetObs;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="space-y-2"
      data-testid="eval-ledger"
      style={{ fontFamily: 'var(--font-results-body)' }}
    >
      {/* ── Drawer 1: Goal checks ──────────────────────────────────── */}
      {hasGoals && (
        <EvalLedgerDrawer
          title="Goal checks"
          summary={goalChecksSummary(spatialGoalResults!)}
          icon={FlagIcon}
          guideSection="goal-achievements"
          data-testid="ledger-drawer-goals"
        >
          <GoalResultsSection
            spatialGoalResults={spatialGoalResults!}
            // allowExpand defaults to true for all roles — 2026-09-21 report
            // redesign retires the isCoach-only expand gate (owner decision 2).
          />
        </EvalLedgerDrawer>
      )}

      {/* ── Drawer 2: Phase breakdown ──────────────────────────────── */}
      {hasPhases && (
        <EvalLedgerDrawer
          title="Phase breakdown"
          summary={phaseBreakdownSummary(phaseScores)}
          icon={ChartBarIcon}
          guideSection="phase-breakdown"
          data-testid="ledger-drawer-phases"
        >
          <div className="space-y-4">
            <CompactPhaseScores
              phaseScores={phaseScores}
              onPhaseClick={onPhaseClick}
              showScores={showPhaseScores ?? isCoach}
            />
            {/* FormScoreBreakdown: coach-only — BDS/JAS are internal metrics */}
            {isCoach && showFormBreakdown && (
              <div
                data-testid="ledger-form-score-breakdown"
                style={{
                  borderTop: '1px solid var(--results-surface-line)',
                  paddingTop: '16px',
                }}
              >
                <p
                  className="text-xs font-semibold uppercase tracking-wider mb-3"
                  style={{ color: 'var(--results-text-faint)' }}
                >
                  Form score detail
                </p>
                <FormScoreBreakdown
                  phaseScores={phaseScores}
                  legacyRegionScores={legacyRegionScores}
                />
              </div>
            )}
          </div>
        </EvalLedgerDrawer>
      )}

      {/* ── Drawer 3: Movement economy — coach-only ────────────────── */}
      {hasEconomy && (
        <EvalLedgerDrawer
          title="Movement economy"
          summary={economySummary(economyOfMotion, eomNarrative)}
          icon={BoltIcon}
          guideSection="economy-of-motion"
          data-testid="ledger-drawer-economy"
        >
          <MovementEconomySection
            economyOfMotion={economyOfMotion ?? {}}
            eomNarrative={eomNarrative}
          />
        </EvalLedgerDrawer>
      )}

      {/* ── Drawer 4: Visual & movement analysis — all users ──────── */}
      {hasVisualMovement && (
        <EvalLedgerDrawer
          title="Visual & movement analysis"
          summary={visualMovementSummary(geminiObservations, deterministicObservations)}
          icon={VideoCameraIcon}
          guideSection="gemini-visual"
          data-testid="ledger-drawer-visual"
        >
          <div className="space-y-6">
            {hasGemini && (
              <div data-testid="ledger-gemini-observations">
                <GeminiObservationSection observations={geminiObservations!} />
              </div>
            )}
            {hasDetObs && (
              <div
                data-testid="ledger-movement-analysis"
                data-guide-section="movement-analysis"
                style={
                  hasGemini
                    ? { borderTop: '1px solid var(--results-surface-line)', paddingTop: '16px' }
                    : undefined
                }
              >
                <MovementAnalysis
                  observations={deterministicObservations!}
                  noLlmFallback={noLlmFallback ?? false}
                />
              </div>
            )}
          </div>
        </EvalLedgerDrawer>
      )}

      {/* ── Drawer 5: Technical diagnostics — all users ───────────── */}
      {/*
          DebugDiagnosticsPanel is itself a CollapsibleSection (already default-closed).
          In the flat stack it was a standalone section. Here we render it directly —
          it already carries its own open/close state and title "Technical Diagnostics",
          so we don't need to wrap it in another EvalLedgerDrawer.
          The outer EvalLedgerDrawer wrapper would create a double-nested collapsible
          (open the ledger drawer → then open the panel inside), which is confusing UX.
          Instead we give DebugDiagnosticsPanel the same visual treatment by rendering
          it with the ledger's surface style via a wrapper div.
      */}
      <div
        data-testid="ledger-drawer-diagnostics"
        data-guide-section="technical-diagnostics"
        style={{
          borderRadius: 'var(--results-radius-card)',
          overflow: 'hidden',
        }}
      >
        <DebugDiagnosticsPanel
          pricingResult={pricingResult}
          spatialGoalResults={spatialGoalResults ?? null}
          phaseScores={phaseScores}
          legacyRegionScores={legacyRegionScores}
          economyOfMotion={economyOfMotion}
          eomNarrative={eomNarrative}
        />
      </div>
    </div>
  );
}

export default EvalLedger;
